import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Compra, Proveedor } from '@/types/database'

export interface CompraFiltros {
  estado?: 'pendiente' | 'recibida' | 'anulada'
}

export function useProveedores() {
  return useQuery({
    queryKey: ['proveedores'],
    queryFn: () => api.get<Proveedor[]>('/api/proveedores'),
    staleTime: 60_000,
  })
}

export function useCompras(filtros: CompraFiltros = {}) {
  return useQuery({
    queryKey: ['compras', filtros],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '200' })
      if (filtros.estado) params.set('estado', filtros.estado)
      return api.get<Compra[]>(`/api/compras?${params}`)
    },
    staleTime: 30_000,
  })
}

export function useCrearProveedor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ nombre, telefono }: { nombre: string; telefono?: string }) =>
      api.post<Pick<Proveedor, 'id' | 'nombre'>>('/api/proveedores', {
        nombre: nombre.trim(),
        telefono: telefono?.trim() || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proveedores'] }),
  })
}

export interface CompraItemInput {
  productoId: string
  presentacionId: string | null
  cantidad: number
  precioCosto: number
}

export function useCrearCompra() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ proveedorId, items, notas }: {
      proveedorId: string; items: CompraItemInput[]; notas: string; usuarioId: string
    }) => {
      const total = items.reduce((s, i) => s + i.cantidad * i.precioCosto, 0)
      return api.post<{ id: string; numero: number }>('/api/compras', {
        proveedor_id: proveedorId,
        total,
        notas: notas.trim() || null,
        items: items.map(i => ({
          producto_id:     i.productoId,
          presentacion_id: i.presentacionId,
          cantidad:        i.cantidad,
          precio_costo:    i.precioCosto,
          subtotal:        i.cantidad * i.precioCosto,
        })),
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compras'] }),
  })
}

export function useRecibirCompra() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ compraId, items, depositoId }: {
      compraId: string
      items: Array<{ productoId: string; cantidad: number }>
      depositoId: string
      usuarioId: string
    }) =>
      api.put(`/api/compras/${compraId}/recibir`, {
        deposito_id: depositoId,
        items: items.map(i => ({ producto_id: i.productoId, cantidad: i.cantidad })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compras'] })
      qc.invalidateQueries({ queryKey: ['productos'] })
      qc.invalidateQueries({ queryKey: ['stock-page'] })
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard-alertas'] })
      qc.invalidateQueries({ queryKey: ['dashboard-movimientos'] })
      qc.invalidateQueries({ queryKey: ['movimientos-recientes'] })
    },
  })
}

export function useAnularCompra() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (compraId: string) =>
      api.put(`/api/compras/${compraId}`, { estado: 'anulada' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compras'] }),
  })
}
